import { FC } from "react";

import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
} from "@mui/material";

import {
  MetricCell,
  MetricCellLabel,
  MetricCellValue,
  MetricCellValueRow,
  MetricDeltaChip,
  MetricGroupTitle,
  MetricSubCard,
  MetricSubCardBody,
  MetricSubCardHeader,
  MetricsContainerCard,
  MetricsContainerHeader,
  MetricsContainerTitle,
  MetricsInnerGrid,
} from "./styles";
import {
  DashboardMetric,
  DashboardPeriod,
  OperatorDashboardStats,
  TimeSavedDuration,
} from "./types";

const PERIOD_OPTIONS: Array<{ value: DashboardPeriod; label: string }> = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "quarter", label: "This Quarter" },
  { value: "year", label: "This Year" },
  { value: "all", label: "All Time" },
];

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const integerFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

const formatUSD = (value: number | null) =>
  value === null ? "—" : usdFormatter.format(value);

const formatInteger = (value: number | null) =>
  value === null ? "—" : integerFormatter.format(value);

const formatPercent = (value: number | null) =>
  value === null ? "—" : `${value.toFixed(1)}%`;

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DAYS_PER_MONTH = 30;
const MONTHS_PER_YEAR = 12;

// Time saved grows with every proposal, so raw hours quickly turn into a
// four-digit number. Roll it up into the largest units that fit (using
// 30-day months / 12-month years) and keep only the two most significant
// non-zero parts so the tile stays short — e.g. "2mo 4d", "3h 20m", "0m".
const formatDuration = ({ hours, minutes }: TimeSavedDuration) => {
  const totalMinutes = Math.max(
    0,
    Math.round(hours * MINUTES_PER_HOUR + minutes),
  );

  const totalHours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const totalDays = Math.floor(totalHours / HOURS_PER_DAY);
  const totalMonths = Math.floor(totalDays / DAYS_PER_MONTH);

  const parts: Array<[number, string]> = [
    [Math.floor(totalMonths / MONTHS_PER_YEAR), "y"],
    [totalMonths % MONTHS_PER_YEAR, "mo"],
    [totalDays % DAYS_PER_MONTH, "d"],
    [totalHours % HOURS_PER_DAY, "h"],
    [totalMinutes % MINUTES_PER_HOUR, "m"],
  ];

  const visible = parts
    .filter(([value]) => value > 0)
    .slice(0, 2)
    .map(([value, unit]) => `${value}${unit}`);

  return visible.length > 0 ? visible.join(" ") : "0m";
};

interface MetricDeltaProps {
  delta: number | null;
}

const MetricDelta: FC<MetricDeltaProps> = ({ delta }) => {
  if (delta === null || delta === undefined || !Number.isFinite(delta)) {
    return null;
  }
  const rounded = Math.round(delta * 10) / 10;
  const direction = rounded > 0 ? "up" : rounded < 0 ? "down" : "flat";

  /* Flat period-over-period change used an em dash before "0.0%" — reads as a
     minus sign on mobile. Show a neutral chip with the percentage only. */
  if (direction === "flat") {
    return (
      <MetricDeltaChip direction="flat">
        {rounded.toFixed(1)}%
      </MetricDeltaChip>
    );
  }

  const arrow = direction === "up" ? "▲" : "▼";
  const sign = direction === "up" ? "+" : "";
  return (
    <MetricDeltaChip direction={direction}>
      {arrow} {sign}
      {Math.abs(rounded).toFixed(1)}%
    </MetricDeltaChip>
  );
};

interface MetricCellProps {
  label: string;
  value: string;
  delta?: number | null;
  showDelta?: boolean;
}

// Single metric tile: label on top; value and delta stacked on mobile, inline on desktop.
const MetricTile: FC<MetricCellProps> = ({
  label,
  value,
  delta = null,
  showDelta = true,
}) => (
  <MetricCell>
    <MetricCellLabel>{label}</MetricCellLabel>
    <MetricCellValueRow>
      <MetricCellValue>{value}</MetricCellValue>
      {showDelta && <MetricDelta delta={delta} />}
    </MetricCellValueRow>
  </MetricCell>
);

interface MetricFromMetricProps {
  label: string;
  metric: DashboardMetric;
  format: (value: number | null) => string;
  showDelta?: boolean;
}

const MetricLine: FC<MetricFromMetricProps> = ({
  label,
  metric,
  format,
  showDelta = true,
}) => (
  <MetricTile
    label={label}
    value={format(metric.current)}
    delta={metric.deltaPercent}
    showDelta={showDelta}
  />
);

interface MetricsSectionProps {
  stats?: OperatorDashboardStats;
  isLoading: boolean;
  period: DashboardPeriod;
  onPeriodChange: (period: DashboardPeriod) => void;
}

export const MetricsSection: FC<MetricsSectionProps> = ({
  stats,
  isLoading,
  period,
  onPeriodChange,
}) => {
  const handleChange = (event: SelectChangeEvent<DashboardPeriod>) => {
    onPeriodChange(event.target.value as DashboardPeriod);
  };

  // For "today" there is no prior-period comparison to anchor a trend against
  // (and "all time" doesn't have one either), so suppress the delta chips.
  const showDelta = period !== "today" && period !== "all";

  return (
    <MetricsContainerCard>
      <MetricsContainerHeader>
        <MetricsContainerTitle>Performance</MetricsContainerTitle>
        <FormControl
          sx={{
            minWidth: { xs: 140, md: 160 },
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Select
            value={period}
            onChange={handleChange}
            displayEmpty
            aria-label="Select time period"
          >
            {PERIOD_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </MetricsContainerHeader>

      <MetricsInnerGrid>
        {isLoading || !stats ? (
          <>
            <SkeletonSubCard cells={3} />
            <SkeletonSubCard cells={4} />
          </>
        ) : (
          <>
            <MetricSubCard>
              <MetricSubCardHeader>
                <MetricGroupTitle>Proposals</MetricGroupTitle>
              </MetricSubCardHeader>
              <MetricSubCardBody>
                <MetricLine
                  label="Proposals"
                  metric={stats.proposals.count}
                  format={formatInteger}
                  showDelta={showDelta}
                />
                <MetricLine
                  label="Total Value"
                  metric={stats.proposals.amount}
                  format={formatUSD}
                  showDelta={showDelta}
                />
                <MetricTile
                  label="Time Saved"
                  value={formatDuration(stats.timeSaved.duration)}
                  delta={stats.timeSaved.deltaPercent}
                  showDelta={showDelta}
                />
              </MetricSubCardBody>
            </MetricSubCard>

            <MetricSubCard>
              <MetricSubCardHeader>
                <MetricGroupTitle>Booked Trips</MetricGroupTitle>
              </MetricSubCardHeader>
              <MetricSubCardBody>
                <MetricLine
                  label="Trips"
                  metric={stats.bookedTrips.count}
                  format={formatInteger}
                  showDelta={showDelta}
                />
                <MetricLine
                  label="Total Value"
                  metric={stats.bookedTrips.value}
                  format={formatUSD}
                  showDelta={showDelta}
                />
                <MetricLine
                  label="Avg. Margin"
                  metric={stats.bookedTrips.averageMargin}
                  format={formatPercent}
                  showDelta={showDelta}
                />
                <MetricLine
                  label="Conversion Rate"
                  metric={stats.bookedTrips.conversionRate}
                  format={formatPercent}
                  showDelta={showDelta}
                />
              </MetricSubCardBody>
            </MetricSubCard>
          </>
        )}
      </MetricsInnerGrid>
    </MetricsContainerCard>
  );
};

const SkeletonSubCard: FC<{ cells: number }> = ({ cells }) => (
  <MetricSubCard>
    <MetricSubCardHeader>
      <Skeleton variant="text" width="40%" height={16} />
    </MetricSubCardHeader>
    <MetricSubCardBody>
      {Array.from({ length: cells }).map((_, idx) => (
        <MetricCell key={idx}>
          <Skeleton variant="text" width="80%" height={14} />
          <Skeleton variant="text" width="60%" height={28} />
        </MetricCell>
      ))}
    </MetricSubCardBody>
  </MetricSubCard>
);
